// ─────────────────────────────────────────────────────────────────────────────
// V2SupplierLedger — /dashboard/supplier-ledger in the v2 design.
// Endpoints are v1's: GET /api/supplier-ledger, GET /:supplierId (paged + download),
// POST /:supplierId/payment { amount, description, paymentMode, date }.
// Purchases are debits (you owe), payments are credits; balance > 0 = you owe.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import usePageTitle from "../hooks/usePageTitle";
import userContext from "../context/user/userContext";
import API_BASE from "../apiConfig";
import { Glyph, Duo } from "./icons";
import { MenuButton, Money, Segmented, Sheet, Skel, inr, inrCompact, useMedia } from "./primitives";
import { Field, PageHead } from "./kit";

const PER_PAGE = 20;
const auth = () => ({ "auth-token": localStorage.getItem("token") });
const today = () => new Date().toISOString().split("T")[0];
const fmtDate = (d, o = { day: "numeric", month: "short", year: "numeric" }) => (d ? new Date(d).toLocaleDateString("en-IN", o) : "—");
const ini = (n = "") => n.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";
const MODES = ["Cash", "Bank Transfer", "UPI", "Cheque", "NEFT", "RTGS", "Other"];

export default function V2SupplierLedger({ showAlert }) {
  usePageTitle("Supplier Ledger");
  const navigate = useNavigate();
  const { hasPermission } = useContext(userContext);
  const canWrite = hasPermission("suppliers", "write");
  const isNarrow = useMedia("(max-width: 900px)");
  const [list, setList] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("due");
  const [selId, setSelId] = useState(null);
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState({ totalDebit: 0, totalCredit: 0, balance: 0 });
  const [pg, setPg] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [dl, setDl] = useState(null);

  const loadList = useCallback(async () => {
    setLoadingList(true);
    try { const r = await fetch(`${API_BASE}/api/supplier-ledger`, { headers: auth() }); const d = await r.json(); setList(Array.isArray(d) ? d : []); }
    catch { setList([]); } finally { setLoadingList(false); }
  }, []);
  useEffect(() => { loadList(); }, [loadList]);
  const loadDetail = useCallback(async (id, p = 1) => {
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/api/supplier-ledger/${id}?${new URLSearchParams({ page: p, limit: PER_PAGE })}`, { headers: auth() });
      const d = await r.json();
      setEntries(Array.isArray(d.entries) ? d.entries : []); setPg(d.pagination || null); setPage(p);
      if (d.summary) setSummary({ totalDebit: d.summary.totalDebit || 0, totalCredit: d.summary.totalCredit || 0, balance: d.summary.balance || 0 });
    } catch { setEntries([]); } finally { setLoading(false); }
  }, []);
  useEffect(() => { if (selId) loadDetail(selId, 1); }, [selId, loadDetail]);

  const sorted = useMemo(() => {
    const t = q.trim().toLowerCase();
    return list.filter((s) => !t || `${s.supplierName} ${s.supplierGSTIN || ""}`.toLowerCase().includes(t))
      .sort((a, b) => (sort === "due" ? (b.balance || 0) - (a.balance || 0) : a.supplierName.localeCompare(b.supplierName)));
  }, [list, q, sort]);
  useEffect(() => { if (!isNarrow && !selId && sorted.length) setSelId(sorted[0]._id); }, [sorted, isNarrow, selId]);
  const sel = list.find((s) => s._id === selId) || null;
  const owed = list.reduce((a, s) => a + Math.max(s.balance || 0, 0), 0);
  const owedTo = list.filter((s) => (s.balance || 0) > 0).length;

  const download = async () => {
    try {
      const p = new URLSearchParams({ download: "true" }); if (dl?.from) p.set("from", dl.from); if (dl?.to) p.set("to", dl.to);
      const r = await fetch(`${API_BASE}/api/supplier-ledger/${sel._id}?${p}`, { headers: auth() });
      const d = await r.json();
      const rows = [["Date", "Description", "Debit", "Credit", "Balance"], ...(d.entries || []).map((e) => [fmtDate(e.date), `"${(e.description || "").replace(/"/g, '""')}"`, e.type === "debit" ? e.amount.toFixed(2) : "", e.type === "credit" ? e.amount.toFixed(2) : "", (e.balance || 0).toFixed(2)])];
      const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([rows.map((r2) => r2.join(",")).join("\n")], { type: "text/csv" }));
      a.download = `SupplierLedger_${sel.supplierName}_${today()}.csv`; a.click(); setDl(null);
    } catch { setDl((x) => ({ ...x, error: "Couldn't download." })); }
  };

  const detail = sel && (
    <div className="v2l-detail" key={sel._id}>
      <div className="v2l-hero">
        <span className="v2-avatar v2-avatar--client" style={{ width: 56, height: 56, fontSize: 20 }}>{ini(sel.supplierName)}</span>
        <div className="v2l-hero-text"><h2>{sel.supplierName}</h2>{sel.supplierGSTIN && <span className="v2-row-sub v2-mono">{sel.supplierGSTIN}</span>}</div>
        <div className="v2l-hero-actions">
          {canWrite && <button type="button" className="v2-btn v2-btn--primary" onClick={() => setPayOpen(true)}><Glyph name="cash" size={16} /> Record payment</button>}
          <MenuButton className="v2-icon-btn v2-icon-btn--soft" label="Ledger options" width={230} items={[
            { label: "Download CSV", glyph: "download", onSelect: () => setDl({}) },
            { label: "Purchase bills", glyph: "invoice", onSelect: () => navigate("/dashboard/purchase-entry") },
          ]} />
        </div>
      </div>
      <div className="v2l-stats">
        <div className="v2l-stat"><span>Purchased</span><strong>{inr(summary.totalDebit, { decimals: 2 })}</strong></div>
        <div className="v2l-stat"><span>Paid</span><strong className="is-good">{inr(summary.totalCredit, { decimals: 2 })}</strong></div>
        <div className={`v2l-stat v2l-stat--hero${summary.balance > 0 ? "" : " is-clear"}`}><span>{summary.balance > 0 ? "You owe" : summary.balance < 0 ? "Advance paid" : "Settled"}</span><strong><Money value={Math.abs(summary.balance)} /></strong></div>
      </div>
      <div className="v2c-section-title">Statement</div>
      <div className={`v2-card v2l-statement${loading ? " is-refreshing" : ""}`}>
        {loading && !entries.length ? <div className="v2-list" style={{ padding: 8 }}>{[0, 1, 2, 3].map((i) => <Skel key={i} h={52} r={14} />)}</div>
          : entries.length === 0 ? <div className="v2c-empty"><Duo name="ledger" size={44} /><span>No transactions yet.</span></div>
          : entries.map((e) => {
            const paid = e.type === "credit";
            return (
              <div key={e._id} className="v2l-entry">
                <span className="v2pub-day"><strong>{new Date(e.date).getDate()}</strong><span>{fmtDate(e.date, { month: "short" })}</span></span>
                <span className="v2l-entry-main">
                  <span className="v2l-entry-desc">{e.description || (paid ? "Payment made" : "Purchase")}</span>
                  <span className="v2l-entry-meta">{fmtDate(e.date, { year: "numeric" })}{e.paymentMode && <span className="v2l-tag">{e.paymentMode}</span>}</span>
                </span>
                <span className="v2l-entry-amts">
                  <span className={`v2l-amt${paid ? " is-credit" : ""}`}>{paid ? "−" : "+"}{inr(e.amount, { decimals: 2 })}</span>
                  <span className="v2l-bal">Bal {inr(e.balance, { decimals: 2 })}</span>
                </span>
              </div>);
          })}
      </div>
      {pg && pg.totalPages > 1 && (
        <nav className="v2i-pager">
          <button type="button" className="v2-btn v2-btn--sm" disabled={page <= 1 || loading} onClick={() => loadDetail(selId, page - 1)}><Glyph name="chevronLeft" size={16} stroke={2} /> Newer</button>
          <span>Page <b>{page}</b> of {pg.totalPages}</span>
          <button type="button" className="v2-btn v2-btn--sm" disabled={page >= pg.totalPages || loading} onClick={() => loadDetail(selId, page + 1)}>Older <Glyph name="chevronRight" size={16} stroke={2} /></button>
        </nav>
      )}
    </div>
  );

  const detailOnly = isNarrow && sel;
  return (
    <div className="v2p v2l">
      {!detailOnly && <PageHead eyebrow="Payables" title="Supplier ledger" sub={loadingList ? "Loading…" : `You owe ${inr(owed)} to ${owedTo} supplier${owedTo === 1 ? "" : "s"}`} />}
      {detailOnly && <button type="button" className="v2l-back" onClick={() => setSelId(null)}><Glyph name="chevronLeft" size={18} stroke={2} /> All suppliers</button>}
      <div className={`v2c-split${detailOnly ? " is-detail" : ""}`}>
        {!detailOnly && (
          <section className="v2-card v2c-listcard">
            <div className="v2-field--search v2c-search"><Glyph name="search" size={17} /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search suppliers" aria-label="Search suppliers" /></div>
            <div className="v2l-sort"><Segmented size="sm" ariaLabel="Sort" value={sort} onChange={setSort} options={[{ value: "due", label: "Most owed" }, { value: "name", label: "A–Z" }]} /></div>
            {loadingList ? <div className="v2-list" style={{ marginTop: 10 }}>{[0, 1, 2, 3].map((i) => <Skel key={i} h={54} r={16} />)}</div>
              : sorted.length === 0 ? <div className="v2c-empty"><Duo name="building" size={44} /><span>{q ? "No suppliers match." : "No suppliers yet."}</span></div>
              : <div className="v2c-list">{sorted.map((s) => (
                <button key={s._id} type="button" className={`v2l-row${s._id === selId && !isNarrow ? " is-sel" : ""}`} onClick={() => setSelId(s._id)}>
                  <span className="v2-avatar v2-avatar--client" style={{ width: 38, height: 38, fontSize: 13.5 }}>{ini(s.supplierName)}</span>
                  <span className="v2-row-main"><span className="v2-row-title">{s.supplierName}</span><span className="v2-row-sub">{(s.balance || 0) > 0 ? "You owe" : (s.balance || 0) < 0 ? "Advance paid" : "Settled"}</span></span>
                  <span className={`v2l-row-bal${(s.balance || 0) > 0 ? " is-due" : ""}`}>{inrCompact(Math.abs(s.balance || 0))}</span>
                </button>))}</div>}
          </section>
        )}
        {(!isNarrow || sel) && <section className={isNarrow ? "" : "v2-card v2c-detailcard"}>{detail || <div className="v2c-empty"><Duo name="ledger" size={44} /><span>Choose a supplier.</span></div>}</section>}
      </div>
      {sel && <PaySheet open={payOpen} supplier={sel} owed={summary.balance} onClose={() => setPayOpen(false)}
        onDone={async (m) => { setPayOpen(false); showAlert?.(m, "success"); await loadList(); loadDetail(selId, 1); }} />}
      <Sheet open={!!dl} onClose={() => setDl(null)} width={440} title="Download ledger" subtitle={sel ? `${sel.supplierName} · CSV` : ""}
        footer={<><button type="button" className="v2-btn" onClick={() => setDl(null)}>Cancel</button><button type="button" className="v2-btn v2-btn--primary" onClick={download}>Download CSV</button></>}>
        <div className="v2-form"><p className="v2-row-sub">Leave dates empty for the full history.</p>
          <div className="v2-form-row">
            <Field label="From" optional><input className="v2-input" type="date" value={dl?.from || ""} onChange={(e) => setDl((x) => ({ ...x, from: e.target.value }))} /></Field>
            <Field label="To" optional><input className="v2-input" type="date" value={dl?.to || ""} onChange={(e) => setDl((x) => ({ ...x, to: e.target.value }))} /></Field>
          </div>{dl?.error && <div className="v2-banner v2-banner--bad">{dl.error}</div>}</div>
      </Sheet>
    </div>
  );
}

function PaySheet({ open, supplier, owed, onClose, onDone }) {
  const [amount, setAmount] = useState(""); const [description, setDescription] = useState(""); const [mode, setMode] = useState("Cash"); const [date, setDate] = useState(today());
  const [busy, setBusy] = useState(false); const [err, setErr] = useState("");
  useEffect(() => { if (open) { setAmount(""); setDescription(""); setMode("Cash"); setDate(today()); setErr(""); } }, [open]);
  const amt = parseFloat(amount) || 0;
  const go = async (e) => {
    e.preventDefault(); setErr("");
    if (!(amt > 0)) { setErr("Enter an amount greater than 0."); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/api/supplier-ledger/${supplier._id}/payment`, { method: "POST", headers: { "Content-Type": "application/json", ...auth() }, body: JSON.stringify({ amount: amt, description, paymentMode: mode, date }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || "Couldn't record the payment");
      onDone(`Payment of ${inr(amt, { decimals: 2 })} to ${supplier.supplierName} recorded`);
    } catch (x) { setErr(x.message); } finally { setBusy(false); }
  };
  return (
    <Sheet open={open} onClose={onClose} width={480} title="Record payment" subtitle={supplier.supplierName}
      footer={<><button type="button" className="v2-btn" onClick={onClose}>Cancel</button><button type="submit" form="v2sl-pay" className="v2-btn v2-btn--primary" disabled={busy}>{busy ? "Saving…" : amt > 0 ? `Pay ${inr(amt, { decimals: 2 })}` : "Record payment"}</button></>}>
      <form id="v2sl-pay" className="v2-form" onSubmit={go}>
        <Field label="Amount paid" hint={owed > 0 ? `You currently owe ${inr(owed, { decimals: 2 })}` : undefined}>
          <span className="v2-input-wrap v2-input-wrap--money"><span className="v2-input-prefix">₹</span>
            <input className="v2-input v2-input--lg" type="number" step="0.01" min="0" inputMode="decimal" autoFocus value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" />
            {owed > 0 && <button type="button" className="v2-chip-btn" onClick={() => setAmount(String(owed.toFixed(2)))}>All owed</button>}</span>
        </Field>
        <div className="v2-form-row">
          <Field label="Date"><input className="v2-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} /></Field>
          <Field label="Mode"><select className="v2-input" value={mode} onChange={(e) => setMode(e.target.value)}>{MODES.map((m) => <option key={m}>{m}</option>)}</select></Field>
        </div>
        <Field label="Description" optional><input className="v2-input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="e.g. NEFT against October bills" /></Field>
        {err && <div className="v2-banner v2-banner--bad"><Glyph name="alert" size={16} /> {err}</div>}
      </form>
    </Sheet>
  );
}
